import { useTranslation } from 'module/hooks/use-translation';
import { TableDataType } from 'module/ui/components/atoms/rich-text-editor/toolbar/table-toolbar';
import { JSONContent } from '@tiptap/core';
import { NodeViewContent } from '@tiptap/react';
import {
  CustomSectionState,
  CustomSectionContentState,
  CustomSectionType,
} from 'api/custom-section/types';
import { AnswerLength } from 'api/custom-section/types/question-answer-item';
import { useCallback, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { LoaderStreamingComponent } from 'components/common/loader-streaming';
import { QuestionAnswerItemPromptType } from 'components/organisms/document-editor/extensions/custom-section/types';
import { PromptInput } from 'components/organisms/document-editor/extensions/custom-section/ui/sections-type/multi-question/prompt/prompt-input';
import {
  SectionPreview,
  SectionTitle,
  SectionWrapper,
} from 'components/organisms/document-editor/extensions/custom-section/ui/shared/section-styles';
import { defaultCustomSectionNodeConfig } from 'components/organisms/document-editor/extensions/custom-section/utils/default-config';
import { CustomGroupSectionComponentProps } from '../types';

const extractPromptFromChildren = (node: {
  content: {
    forEach: (
      fn: (child: {
        type: { name: string };
        attrs: Record<string, unknown>;
      }) => void,
    ) => void;
  };
  attrs?: Record<string, unknown>;
}): QuestionAnswerItemPromptType => {
  const questions: QuestionAnswerItemPromptType['questions'] = [];

  node.content.forEach(
    (child: { type: { name: string }; attrs: Record<string, unknown> }) => {
      if (
        child.type.name === 'RL_CUSTOM_SECTION' &&
        child.attrs.sectionType === CustomSectionType.QuestionAnswerItem
      ) {
        const config = (child.attrs.config as Record<string, unknown>) || {};
        const choicesRaw = config.choices;
        const choicesStrings =
          Array.isArray(choicesRaw) &&
          choicesRaw.every((c) => typeof c === 'string')
            ? (choicesRaw as string[])
            : Array.isArray(choicesRaw)
              ? (choicesRaw as { label?: string }[]).map((c) =>
                  typeof c === 'string' ? c : (c?.label ?? ''),
                )
              : [];
        const choicesForMeta = choicesStrings.map((label, i) => ({
          label,
          value: i,
        }));

        const dataType =
          (config.dataType as TableDataType) ?? TableDataType.TEXT;

        questions.push({
          label: (config.question as string) ?? '',
          value: questions.length,
          position: (config.index as number) ?? questions.length + 1,
          fieldOptionMeta: {
            id: (child.attrs.templateSectionId ??
              child.attrs.documentSectionId ??
              uuidv4()) as string,
            optionType: dataType,
            answerFrom: config.answerFrom as string | undefined,
            answerLength:
              dataType === TableDataType.TEXT
                ? ((config.answerLength as AnswerLength) ??
                  AnswerLength.TwentyWords)
                : null,
            choices: choicesForMeta,
            generateNewChoices: (config.generateNewChoices as boolean) ?? false,
            aiContext: config.aiContext as string | undefined,
          },
        });
      }
    },
  );

  const title = ((node.attrs?.title as string) ?? '') as string;

  if (questions.length === 0) {
    return {
      title,
      questions: [
        {
          label: '',
          value: 0,
          position: 1,
          fieldOptionMeta: {
            id: uuidv4(),
            optionType: TableDataType.TEXT,
            choices: [],
            generateNewChoices: false,
          },
        },
      ],
    };
  }

  return { title, questions };
};

export const MultiQuestionsGroupSectionPrompt = ({
  node,
  editor,
  onRemove,
  onDone,
  onUndo,
  replaceContent,
  pageType,
  updateAttributes,
  resolver,
  allowResolve,
}: CustomGroupSectionComponentProps) => {
  const showPrompt = node.attrs.state === CustomSectionState.Open;
  const defaultTitleLabel = useTranslation(
    'rich_text_editor.sections.question_section.title',
  );
  const initialPrompt = useMemo(
    () =>
      node.attrs.contentState === CustomSectionContentState.Prompt &&
      node.content.size > 0
        ? extractPromptFromChildren(
            node as Parameters<typeof extractPromptFromChildren>[0],
          )
        : {
            title: (node.attrs.title as string) ?? '',
            questions: [
              {
                label: '',
                value: 0,
                position: 1,
                fieldOptionMeta: {
                  id: uuidv4(),
                  optionType: TableDataType.TEXT,
                  choices: [],
                  generateNewChoices: false,
                },
              },
            ],
          },
    [node],
  );

  const [prompt, setPrompt] =
    useState<QuestionAnswerItemPromptType>(initialPrompt);
  const [isGenerating, setIsGenerating] = useState(false);

  const buildCustomSectionNodes = useCallback(
    (
      userPrompt: QuestionAnswerItemPromptType,
      forStreaming: boolean,
    ): JSONContent[] =>
      userPrompt.questions.map((question, index) => {
        const baseAttrs = defaultCustomSectionNodeConfig[
          CustomSectionType.QuestionAnswerItem
        ] as {
          title: string;
          config: Record<string, unknown>;
          sectionType: string;
          contentState: string;
          state: string;
        };

        const tableContent = [
          {
            type: 'tableRow',
            content: [
              {
                type: 'tableCell',
                attrs: { width: '10%' },
                content: [{ type: 'text', text: `${index + 1}.` }],
              },
              {
                type: 'tableCell',
                content: [{ type: 'text', text: question.label }],
              },
            ],
          },
          {
            type: 'tableRow',
            content: [
              { type: 'tableCell', content: [] },
              {
                type: 'tableCell',
                content: [
                  {
                    type: 'RL_PLACEHOLDER_CONFIG',
                    attrs: {
                      icon: '',
                      iconPosition: 'left',
                    },
                    content: [
                      {
                        type: 'text',
                        text:
                          question.fieldOptionMeta?.optionType ===
                          TableDataType.DATE
                            ? '1998-01-13'
                            : 'Answer',
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ];

        return {
          type: 'RL_CUSTOM_SECTION',
          attrs: {
            ...baseAttrs,
            title: question.label,
            state: CustomSectionState.Closed,
            contentState: CustomSectionContentState.Generated,
            sectionType: CustomSectionType.QuestionAnswerItem,
            templateSectionId: question.fieldOptionMeta?.id ?? uuidv4(),
            config: {
              question: question.label,
              answer: '',
              dataType:
                question.fieldOptionMeta?.optionType ?? TableDataType.TEXT,
              answerLength:
                question.fieldOptionMeta?.optionType === TableDataType.TEXT
                  ? (question.fieldOptionMeta?.answerLength ??
                    AnswerLength.TwentyWords)
                  : null,
              answerFrom: question.fieldOptionMeta?.answerFrom ?? '',
              choices: (question.fieldOptionMeta?.choices ?? []).map((c) =>
                typeof c === 'string' ? c : c.label,
              ),
              generateNewChoices:
                question.fieldOptionMeta?.generateNewChoices ?? false,
              aiContext: question.fieldOptionMeta?.aiContext ?? '',
              index: index + 1,
            },
          },
          content: forStreaming
            ? [
                {
                  type: 'paragraph',
                },
              ]
            : [{ type: 'table', content: tableContent }],
        };
      }),
    [],
  );

  const onGenerate = useCallback(
    async (userPrompt: QuestionAnswerItemPromptType) => {
      setIsGenerating(true);

      const attributes = {
        ...node.attrs,
        title: userPrompt.title,
        state: CustomSectionState.Closed,
        contentState: CustomSectionContentState.Prompt,
        config: node.attrs.config ?? { description: '' },
      };

      try {
        if (resolver && replaceContent) {
          try {
            const customSectionNodes = buildCustomSectionNodes(
              userPrompt,
              true,
            );

            await onDone(attributes, { content: customSectionNodes });
          } catch {
            const customSectionNodes = buildCustomSectionNodes(
              userPrompt,
              false,
            );

            await onDone(attributes);
            replaceContent(customSectionNodes);
          }
        } else {
          const customSectionNodes = buildCustomSectionNodes(userPrompt, false);

          await onDone(attributes);
          if (replaceContent) {
            replaceContent(customSectionNodes);
          }
        }
      } catch {
        // no-op
      } finally {
        setIsGenerating(false);
      }
    },
    [node.attrs, onDone, replaceContent, resolver, buildCustomSectionNodes],
  );

  const getNodeAsPayload = useCallback(() => {
    const payload = node.toJSON() as Record<string, unknown>;

    if (!('content' in payload)) {
      payload.content = [];
    }

    return payload;
  }, [node]);

  const onClose = useCallback(() => {
    if (node.content?.size === 0) {
      onRemove();

      return;
    }
    setPrompt(
      extractPromptFromChildren(
        node as Parameters<typeof extractPromptFromChildren>[0],
      ),
    );
    updateAttributes({ state: CustomSectionState.Closed });
    onUndo?.();
  }, [onUndo, node, onRemove, updateAttributes]);

  return (
    <div>
      {showPrompt && editor.isEditable ? (
        <PromptInput
          prompt={prompt}
          setPrompt={setPrompt}
          onGenerate={onGenerate}
          onCancel={onRemove}
          onUndo={onClose}
          getNodeAsPayload={getNodeAsPayload}
          allowResolve={allowResolve}
          isActionLoading={isGenerating}
          pageType={pageType}
        />
      ) : (
        <SectionPreview
          onClick={() => {
            if (!isGenerating) {
              updateAttributes({ state: CustomSectionState.Open });
            }
          }}
        >
          {isGenerating && <LoaderStreamingComponent />}
          <SectionWrapper>
            <SectionTitle>{prompt.title || defaultTitleLabel}</SectionTitle>

            <NodeViewContent />
          </SectionWrapper>
        </SectionPreview>
      )}
    </div>
  );
};
